import React, { useMemo, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import styled from "styled-components";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, message, Modal, Space, Tag, Tooltip } from "antd";
import { has, isNil, pickBy } from "lodash";

import { LoggedInUserContext } from "context/LoggedInUserContext";
import { useCustomers, useUsers, useApps } from "misc/api/keycloakApi";
import { buildUrl, generateRandomPassword } from "misc/helpfunctions";
import { clientUrls } from "misc/urls";
import { stsColors } from "../../../styles";
import SideHeader from "navigation/SideHeader";
import { Card } from "misc/card";
import { DatetimeField, TextField } from "misc/fields";
import { EditableTable, PaginatedTableWidget } from "misc/tables";
import { useParams } from "misc/hooks";

const IconComponent = React.forwardRef((props, ref) => <FontAwesomeIcon {...props} forwardedRef={ref} />);

const UserIcon = styled(IconComponent)({
    color: stsColors.grey3
});

const UsersListComponent = () => {
    const { hasAuthorityFull, hasAuthorityLocalAdmin } = useContext(LoggedInUserContext);
    const { apps } = useApps();
    const { users, usersQueryState, deleteUser, deleteUserMutation, resetPassword, resetPasswordMutation } = useUsers({ suspense: true });
    const { customers, customersQueryState } = useCustomers({ suspense: true, enabled: hasAuthorityFull() });

    const [t] = useTranslation();
    const history = useHistory();

    const [userForPasswordReset, setUserForPasswordReset] = useState(null);
    const [tempPassword, setTempPassword] = useState(null);

    const { params, setParams } = useParams({
        options: [
            {
                name: "emailVerified",
                persist: "site"
            },
            {
                name: "organizationId",
                persist: "site"
            },
            {
                name: "apps",
                persist: "site"
            }
        ]
    });

    const handleTableChange = (pagination, filters, sorter) => {
        setParams({
            emailVerified: isNil(filters.emailVerified) ? undefined : filters.emailVerified?.[0],
            organizationId: filters.organizationId ? filters.organizationId : undefined,
            apps: filters.apps ? filters.apps : undefined
        });
    };

    const onDeleteUser = user =>
        deleteUser(user, {
            onSuccess: () => message.success(t("t_user_delete_success")),
            onError: error => message.error(t(`t_http_error_${error.status}`))
        });

    const onResetPassword = user => {
        resetPassword(user?.id, tempPassword, {
            onSuccess: () => message.success(t("t_reset_password_email_sent")),
            onError: error => message.error(t(`t_http_error_${error.status}`))
        });
    };

    const transformedData = useMemo(() => {
        let transformed = [];

        if (hasAuthorityFull()) {
            transformed = users.map(user => {
                const { organizations, ...rest } = user;
                if (organizations && organizations.length > 0) {
                    const [firstOrganization] = organizations;
                    return {
                        ...rest,
                        organizationId: firstOrganization.id,
                        organizationName: customers.find(c => c.id === firstOrganization.id)?.name || "",
                        editable: true,
                        deletable: true
                    };
                }
                return {
                    ...rest,
                    organizationId: null,
                    organizationName: "",
                    editable: true,
                    deletable: true
                };
            });
        } else if (hasAuthorityLocalAdmin()) {
            transformed = users.map(user => ({
                ...user,
                editable: true,
                deletable: true
            }));
        }

        return transformed;
    }, [users, customers]);

    const columns = [
        {
            key: "emailVerified",
            dataIndex: "emailVerified",
            title: <TextField value="t_user_status" />,
            render: text =>
                text ? (
                    <Tooltip title={<TextField value="t_user_status_verified" />}>
                        <UserIcon icon="user-check" />
                    </Tooltip>
                ) : (
                    <Tooltip title={<TextField value="t_user_status_not_verified" />}>
                        <UserIcon icon="user-lock" />
                    </Tooltip>
                ),
            filterSearch: true,
            filters: [
                { text: t("t_user_status_verified"), value: true },
                { text: t("t_user_status_not_verified"), value: false }
            ],
            onFilter: (value, record) => record.emailVerified === value,
            width: 80,
            fixed: "left"
        },
        {
            key: "fullName",
            dataIndex: "fullName",
            title: <TextField value="t_user_full_name" />,
            render: text => <TextField value={text} />,
            width: 200,
            fixed: "left",
            sorter: (a, b) => a.fullName.localeCompare(b.fullName),
            defaultSortOrder: "ascend"
        },
        {
            key: "email",
            dataIndex: "email",
            title: <TextField value="t_user_email" />,
            sorter: (a, b) => a.email.localeCompare(b.email),
            width: 250
        },
        {
            key: "organizationId",
            dataIndex: "organizationId",
            title: <TextField value="t_customer" />,
            render: text => {
                const customerName = customers.find(customer => customer.id === text)?.name;
                return (
                    customerName && (
                        <Tag key={text}>
                            <TextField value={customerName} />
                        </Tag>
                    )
                );
            },
            filterSearch: true,
            filters: customers.map(customer => ({
                text: customer.name,
                value: customer.id
            })),
            onFilter: (value, record) => {
                return record.organizationId === value;
            },
            visible: hasAuthorityFull()
        },
        {
            key: "apps",
            dataIndex: "permissions",
            title: <TextField value="t_apps" />,
            render: (permissions, record) => {
                if (!permissions) return "";
                return (
                    <span>
                        {apps.map(app => {
                            const hasAppPermission = permissions.some(permission => permission.name.startsWith(`permission_app_${app}`));
                            return (
                                hasAppPermission && (
                                    <Tag key={app}>
                                        <TextField value={`t_app_${app}`} />
                                    </Tag>
                                )
                            );
                        })}
                    </span>
                );
            },
            filters: apps.map(app => ({ text: t(`t_${app}`), value: app })),
            onFilter: (value, record) => {
                return record.permissions.some(permission => permission.name.startsWith(`permission_app_${value}`));
            },
            width: 150
        },
        {
            key: "admin",
            dataIndex: "permissions",
            title: <TextField value="t_admin" />,
            render: (permissions, record) => {
                if (!permissions) return "";
                const isLocalAdmin = permissions.some(permission => permission.name === "permission_authority_local-admin");
                const isFullAdmin = permissions.some(permission => permission.name === "permission_authority_full");
                return (
                    <>
                        {isLocalAdmin && (
                            <Tag>
                                <TextField value="t_permission_authority_local-admin" />
                            </Tag>
                        )}
                        {isFullAdmin && (
                            <Tag>
                                <TextField value="t_permission_authority_full" />
                            </Tag>
                        )}
                    </>
                );
            },
            filters: [
                { text: t("t_permission_authority_local-admin"), value: "local" },
                { text: t("t_permission_authority_full"), value: "full" },
                { text: t("t_user_not_admin"), value: "none" }
            ],
            onFilter: (value, record) => {
                const isLocalAdmin = record.permissions.some(permission => permission.name === "permission_authority_local-admin");
                const isFullAdmin = record.permissions.some(permission => permission.name === "permission_authority_full");
                if (value === "local") return isLocalAdmin && !isFullAdmin;
                if (value === "full") return isFullAdmin;
                if (value === "none") return !isLocalAdmin && !isFullAdmin;
                return false;
            },
            width: 150
        },
        {
            key: "lastLogin",
            dataIndex: "lastLogin",
            title: <TextField value="t_user_last_login" />,
            render: value => (value ? <Tooltip title={<DatetimeField value={value} />}>{moment(value).fromNow()}</Tooltip> : "-"),
            sorter: (a, b) => moment(b.lastLogin ?? 0).unix() - moment(a.lastLogin ?? 0).unix(),
            width: 150
        }
    ];

    return (
        <Card title="t_users" height={700} stretchHeight>
            <EditableTable
                dataSource={transformedData}
                columns={columns}
                scroll={{ x: "max-content" }}
                rowKey="id"
                onDeleteItem={onDeleteUser}
                extraActions={record => [
                    {
                        disabled: !record.editable,
                        tooltip: "t_user_edit",
                        key: `edit ${record.id}`,
                        icon: <FontAwesomeIcon icon={["fal", "pen-to-square"]} />,
                        onClick: () =>
                            history.push(
                                buildUrl({
                                    path: clientUrls.users.edit(),
                                    queryParameters: { userId: record.id }
                                })
                            )
                    },
                    {
                        icon: <FontAwesomeIcon icon={["fal", "key"]} />,
                        onClick: () => {
                            setUserForPasswordReset(record);
                            setTempPassword(generateRandomPassword(10));
                        },
                        tooltip: "t_user_reset_password",
                        key: `reset ${record.id}`
                    }
                ]}
                extraHeader={
                    <Button onClick={() => history.push(clientUrls.users.create())}>
                        <Space>
                            <FontAwesomeIcon icon={["fal", "plus"]} />
                            <TextField value="t_user_create" />
                        </Space>
                    </Button>
                }
                isLoading={
                    usersQueryState.isLoading ||
                    customersQueryState?.isLoading ||
                    usersQueryState.isRefetching ||
                    deleteUserMutation.isLoading ||
                    resetPasswordMutation.isLoading
                }
                filters={{
                    emailVerified: params.emailVerified,
                    organizationId: params.organizationId,
                    apps: params.apps
                }}
                onTableChange={handleTableChange}
            />
            <Modal
                title={<TextField value="t_user_reset_password_title" />}
                open={userForPasswordReset}
                onCancel={() => {
                    setUserForPasswordReset(null);
                    setTempPassword(null);
                }}
                onOk={() => {
                    onResetPassword(userForPasswordReset);
                    setUserForPasswordReset(null);
                    setTempPassword(null);
                }}
                cancelText={<TextField value="t_cancel" />}
                closable
            >
                <Space direction="vertical" size={12}>
                    <Space style={{ padding: 4 }} direction="vertical" size={0}>
                        <TextField value="t_user_reset_password_description_1" />
                        <TextField value="t_user_reset_password_description_2" />
                    </Space>

                    {tempPassword && (
                        <div style={{ background: "#f5f5f5", padding: 8, borderRadius: 4 }}>
                            <Space direction="vertical" size={8}>
                                <TextField value="t_user_reset_password_temporary_password_notice" />
                                <TextField
                                    value={tempPassword}
                                    enableCopy
                                    style={{
                                        background: "#fff",
                                        padding: "4px 8px",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 2
                                    }}
                                />
                            </Space>
                        </div>
                    )}
                </Space>
            </Modal>
        </Card>
    );
};

export default UsersListComponent;
